import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePermissions } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';
import { listAdmissions } from '../services/api/admissionApi';
import { ADMISSION_STATUS, ADMISSION_PERMISSIONS, isAwaitingBed } from '../constants/admission';
import { BED_STATE_PILLS, BED_STATE_META } from '../constants/occupancy';
import useOccupancyStore, {
  selectOccupancyStats,
  selectOccupancyTree,
  selectDeptGroups,
  selectDeptOptions,
} from '../stores/occupancyStore';
import useDepartmentStore from '../stores/departmentStore';
import { postBedState } from '../services/api/occupancyApi';
import eventBus from '../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';
import WardGroup from '../components/admission/board/WardGroup';
import MiniOccupancyBar from '../components/admission/board/MiniOccupancyBar';
import DeptFilter from '../components/admission/board/DeptFilter';
import StateFilterChips from '../components/admission/board/StateFilterChips';
import SearchBox from '../components/admission/board/SearchBox';
import BedPopover from '../components/admission/board/BedPopover';
import AdminPageHeader from '../components/common/AdminPageHeader';

/**
 * OccupancyBoardPage (C2 / FT6) — the live occupancy board shell (Variant A).
 *
 * - Gated on VIEW_OCCUPANCY_BOARD (+ the admission flag via the route's
 *   AdmissionEnabledGate); no permission → an in-page no-access panel, not a crash.
 * - ONE hydrate on mount (single-flight) + ONE forced refetch on realtime
 *   reconnect — bounded requests, no polling loop, no per-bed fetch.
 * - Org totals + per-ward mini-bars come from the SHARED selectOccupancyStats
 *   (single-source-of-truth). While unloaded the selector returns null →
 *   a loading state, never fake zeros.
 * - The census strip is a UI SLOT: admits/discharges/expected-discharge counts
 *   land in C5, so it renders literal "—" placeholders (never a computed 0).
 * - Dept filter GROUPS (never excludes); state filter DIMS; search auto-expands.
 */
export default function OccupancyBoardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canView = hasPermission('VIEW_OCCUPANCY_BOARD');
  // The desk route's own gate (AdmissionDeskPage → VIEW_ADMISSION): no dead link.
  const canOpenDesk = hasPermission(ADMISSION_PERMISSIONS.VIEW_ADMISSION);
  const canManage = hasPermission('MANAGE_BED_STATE');
  const canSetup = hasPermission('MANAGE_BED_CONFIG');

  const wards = useOccupancyStore((s) => s.wards);
  const rooms = useOccupancyStore((s) => s.rooms);
  const beds = useOccupancyStore((s) => s.beds);
  const loaded = useOccupancyStore((s) => s.loaded);
  const isLoading = useOccupancyStore((s) => s.isLoading);
  const error = useOccupancyStore((s) => s.error);
  const hydrate = useOccupancyStore((s) => s.hydrate);
  const refetchOnReconnect = useOccupancyStore((s) => s.refetchOnReconnect);
  const applyOptimistic = useOccupancyStore((s) => s.applyOptimistic);
  const applyServerBedState = useOccupancyStore((s) => s.applyServerBedState);

  const departments = useDepartmentStore((s) => s.departments);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);

  const [deptFilter, setDeptFilter] = useState('');
  const [wardFilter, setWardFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [query, setQuery] = useState('');
  const [activeBed, setActiveBed] = useState(null);
  const [recentBedIds, setRecentBedIds] = useState(() => new Set());

  // ── "n awaiting bed" badge count (S10) — admitted rows with no bed. ONE
  //    admissions fetch, guarded by a loaded ref so re-renders never re-fire it
  //    (bounded — no per-row loop, no request storm). Derived via the shared
  //    isAwaitingBed rule (same source as the desk chip/filter). ──────────────
  const [awaitingBedCount, setAwaitingBedCount] = useState(0);
  const awaitingLoadedRef = useRef(false);
  useEffect(() => {
    if (!canView || awaitingLoadedRef.current) return undefined;
    awaitingLoadedRef.current = true;
    let alive = true;
    listAdmissions({ status: ADMISSION_STATUS.ADMITTED }).then((res) => {
      if (!alive || !res || !res.success) return;
      setAwaitingBedCount((res.data.admissions || []).filter(isAwaitingBed).length);
    });
    return () => { alive = false; };
  }, [canView]);

  // ── Bounded data load: single-flight hydrate + reconnect refetch ──────
  useEffect(() => { if (canView) hydrate(); }, [canView, hydrate]);
  useEffect(() => { if (canView) fetchDepartments(); }, [canView, fetchDepartments]);
  useEffect(() => {
    if (!canView) return undefined;
    const off = eventBus.on(REALTIME_EVENTS.CONNECTED, () => refetchOnReconnect());
    return off;
  }, [canView, refetchOnReconnect]);

  // ── "just now" pulse: mark a bed recently-changed for 5s (bounded) ────
  useEffect(() => {
    if (!canView) return undefined;
    const timers = [];
    const off = eventBus.on(REALTIME_EVENTS.BED_STATE_CHANGED, (evt) => {
      const id = evt?.payload?.bed_id;
      if (!id) return;
      setRecentBedIds((prev) => new Set(prev).add(id));
      timers.push(setTimeout(() => {
        setRecentBedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      }, 5000));
    });
    return () => { off(); timers.forEach(clearTimeout); };
  }, [canView]);

  // Stable store snapshot: rebuilt only when a member primitive/ref changes, so the
  // selector memos below don't re-run every render (request-storm / churn guard).
  const storeState = useMemo(
    () => ({ wards, rooms, beds, loaded }),
    [wards, rooms, beds, loaded],
  );
  const stats = useMemo(() => selectOccupancyStats(storeState), [storeState]);
  const tree = useMemo(() => selectOccupancyTree(storeState), [storeState]);
  const deptOptionIds = useMemo(() => selectDeptOptions(storeState), [storeState]);
  const groups = useMemo(() => selectDeptGroups(storeState, deptFilter), [storeState, deptFilter]);

  // Dept id → name for the filter labels (falls back to the id if unknown).
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of departments || []) m[d.department_id || d.id] = d.name;
    return m;
  }, [departments]);
  const deptOptions = useMemo(
    () => deptOptionIds.map((id) => ({ id, name: deptNameById[id] || id })),
    [deptOptionIds, deptNameById],
  );

  const treeByWard = useMemo(() => {
    const m = {};
    for (const node of tree) m[node.ward.ward_id] = node;
    return m;
  }, [tree]);

  // Ward tag chips (dept names, or Organisation-wide when untagged).
  const wardTags = useCallback((ward) => {
    const ids = ward.department_ids || [];
    if (ids.length === 0) {
      return (
        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-200">
          Organisation-wide
        </span>
      );
    }
    return (
      <span className="flex flex-wrap gap-1">
        {ids.map((id) => (
          <span key={id} className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-medium text-teal-800 dark:bg-teal-900/40 dark:text-teal-100">
            {deptNameById[id] || id}
          </span>
        ))}
      </span>
    );
  }, [deptNameById]);

  const onBedAction = useCallback(async ({ target_state, expected_state, reason }) => {
    if (!activeBed) return { success: false };
    const revert = applyOptimistic(activeBed.bed_id, target_state, { reason });
    const res = await postBedState(activeBed.bed_id, { target_state, expected_state, reason });
    if (res.success) applyServerBedState(res.data);
    else revert(); // 409/422/404 — restore the truth; the popover surfaces the code
    return res;
  }, [activeBed, applyOptimistic, applyServerBedState]);

  // ── No-access / gate ──────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <AdminPageHeader />
        <div className="mx-auto max-w-3xl p-6">
          <div role="alert" data-testid="no-access" className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
            <h2 className="mb-1 text-lg font-semibold">Occupancy Board</h2>
            <p className="text-sm">You do not have access to the Occupancy Board. Contact your administrator for access.</p>
          </div>
        </div>
      </div>
    );
  }

  const renderWard = (ward) => (
    <WardGroup
      key={ward.ward_id}
      ward={ward}
      rooms={treeByWard[ward.ward_id]?.rooms || []}
      stats={stats?.perWard?.[ward.ward_id]}
      tags={wardTags(ward)}
      dimState={stateFilter}
      query={query}
      recentBedIds={recentBedIds}
      onBedClick={(bed) => setActiveBed(bed)}
    />
  );

  const visibleWards = wardFilter ? wards.filter((w) => w.ward_id === wardFilter) : wards;
  const groupedForFilter = deptFilter && groups
    ? {
        pinned: groups.pinned.filter((w) => !wardFilter || w.ward_id === wardFilter),
        rest: groups.rest.filter((w) => !wardFilter || w.ward_id === wardFilter),
      }
    : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <AdminPageHeader />
      <div className="mx-auto max-w-6xl p-4" data-testid="occupancy-board">
      {canOpenDesk && (
        <button
          type="button"
          onClick={() => navigate((location.state && location.state.returnTo) || ROUTES.ADMISSIONS)}
          data-testid="board-back-to-desk"
          className="mb-2 text-sm font-medium text-teal-700 dark:text-teal-300 hover:underline"
        >
          &larr; Manage Admission
        </button>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Occupancy Board</h1>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
        </span>
        {awaitingBedCount > 0 && (() => {
          const badgeCls = 'inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:border-amber-600 dark:bg-amber-900/40 dark:text-amber-100';
          const text = `${awaitingBedCount} awaiting bed`;
          // Link only when the member can open the desk (same gate as the back link).
          return canOpenDesk ? (
            <a
              href={`${ROUTES.ADMISSIONS}?filter=awaiting_bed`}
              data-testid="awaiting-bed-badge"
              className={`${badgeCls} hover:bg-amber-200`}
            >
              {text}
            </a>
          ) : (
            <span data-testid="awaiting-bed-badge" className={badgeCls}>{text}</span>
          );
        })()}
      </div>

      {/* Org totals strip — single-source selector; no fake zeros while loading */}
      {stats ? (
        <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900" data-testid="totals-bar">
          <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              {stats.org.byState.OCCUPIED}<span className="text-sm font-normal text-slate-500"> / {stats.org.total} beds occupied</span>
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {BED_STATE_PILLS.map((p) => `${stats.org.byState[p.code]} ${p.label.toLowerCase()}`).join(' · ')}
            </span>
          </div>
          <MiniOccupancyBar byState={stats.org.byState} total={stats.org.total} />
        </div>
      ) : (
        <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900" data-testid="totals-loading">
          Loading occupancy…
        </div>
      )}

      {/* Census strip — UI SLOT for C5 (admits/discharges). Placeholders, not 0. */}
      <div className="mb-3 grid grid-cols-3 gap-2" data-testid="census-strip">
        {[
          ['Admits today', 'admits'],
          ['Discharges today', 'discharges'],
          ['Expected discharges', 'expected'],
        ].map(([label, key]) => (
          <div key={key} className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-2 text-center dark:border-slate-600 dark:bg-slate-800/60">
            <div className="text-lg font-semibold text-slate-400 dark:text-slate-500" data-testid={`census-${key}`}>—</div>
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400">{label}</div>
            <div className="text-[9px] tracking-wide text-slate-400 dark:text-slate-500">Coming soon</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <DeptFilter
          options={deptOptions}
          value={deptFilter}
          onChange={setDeptFilter}
          deptStats={deptFilter ? stats?.perDept?.[deptFilter] : null}
        />
        <select
          aria-label="Ward filter"
          data-testid="ward-filter"
          value={wardFilter}
          onChange={(e) => setWardFilter(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        >
          <option value="">All wards</option>
          {wards.map((w) => <option key={w.ward_id} value={w.ward_id}>{w.name}</option>)}
        </select>
        <StateFilterChips value={stateFilter} onChange={setStateFilter} />
        <SearchBox value={query} onChange={setQuery} />
      </div>

      {/* Legend */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400" data-testid="legend">
        <span>Legend:</span>
        {BED_STATE_PILLS.map((p) => (
          <span key={p.code} className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: BED_STATE_META[p.code].dot }} />
            {p.label}
          </span>
        ))}
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100" role="alert">
          {error}
        </div>
      )}

      {/* Board */}
      {!loaded && isLoading && (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="board-loading">Loading beds…</div>
      )}

      {loaded && wards.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center dark:border-slate-700 dark:bg-slate-900" data-testid="zero-wards">
          <p className="text-sm text-slate-600 dark:text-slate-300">No wards or beds have been set up yet.</p>
          {canSetup && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.BED_SETUP)}
              className="mt-3 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700"
            >
              Set up beds
            </button>
          )}
        </div>
      )}

      {loaded && wards.length > 0 && (
        <div data-testid="board">
          {groupedForFilter ? (
            <>
              <div className="mb-1 mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {(deptNameById[deptFilter] || deptFilter)} wards ({groupedForFilter.pinned.length})
              </div>
              {groupedForFilter.pinned.length > 0
                ? groupedForFilter.pinned.map(renderWard)
                : <div className="mb-3 text-sm text-slate-500">No wards are linked to this department yet.</div>}
              {groupedForFilter.rest.length > 0 && (
                <>
                  <div className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    All other wards ({groupedForFilter.rest.length})
                  </div>
                  {groupedForFilter.rest.map(renderWard)}
                </>
              )}
            </>
          ) : (
            visibleWards.map(renderWard)
          )}
        </div>
      )}

      {/* Bed detail / manual-action popover — centered overlay, viewport-clamped */}
      {activeBed && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
          data-testid="popover-backdrop"
          onClick={() => setActiveBed(null)}
        >
          <BedPopover
            bed={beds.find((b) => b.bed_id === activeBed.bed_id) || activeBed}
            canManage={canManage}
            onAction={onBedAction}
            onClose={() => setActiveBed(null)}
          />
        </div>
      )}
      </div>
    </div>
  );
}
